import React, { useState, useEffect, useRef } from 'react'
import { Modal, Button, Form, Collapse } from 'react-bootstrap'
import styled from 'styled-components'
import { FaChevronRight } from 'react-icons/fa'
import { ModelHyperparameters, ModelParameter } from '../../types/models/model'
import { useGetDefaultHyperparametersQuery } from '../../services/modelsApi'

interface HyperparameterSettingsModalProps {
  show: boolean
  handleClose: () => void
  onSave: (parameters: any) => void
  initialHyperparameters?: any
  modelId?: string
  projectAnnotationType?: string
}

const StyledModal = styled(Modal)`
  .modal-content {
    background: #2a2a2a;
    color: #ffffff;
  }

  .modal-header {
    border-bottom: 1px solid #404040;
  }

  .modal-footer {
    border-top: 1px solid #404040;
  }

  .form-control {
    background: #1a1a1a;
    border: 1px solid #404040;
    color: #ffffff;

    &:focus {
      background: #1a1a1a;
      border-color: #007bff;
      color: #ffffff;
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  .form-check-input {
    background-color: #1a1a1a;
    border-color: #404040;

    &:checked {
      background-color: #007bff;
      border-color: #007bff;
    }
  }

  .form-label {
    color: #ffffff;
  }

  .text-muted {
    color: rgba(255, 255, 255, 0.7) !important;
  }
`

const Section = styled.div`
  margin-bottom: 2rem;
  padding: 1rem;
  background: #1a1a1a;
  border-radius: 8px;

  h4 {
    color: #ffffff;
    margin-bottom: 0;
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;

    .chevron {
      margin-right: 0.5rem;
      transition: transform 0.2s ease;

      &.open {
        transform: rotate(90deg);
      }
    }
  }

  .section-content {
    margin-top: 1rem;
  }
`

const SubSection = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #2a2a2a;
  border-radius: 6px;

  h5 {
    color: #ffffff;
    margin-bottom: 1rem;
    opacity: 0.8;
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;

    .chevron {
      margin-right: 0.5rem;
      transition: transform 0.2s ease;

      &.open {
        transform: rotate(90deg);
      }
    }
  }

  .subsection-content {
    margin-top: 1rem;
  }
`

const HyperparameterSettingsModal: React.FC<HyperparameterSettingsModalProps> = ({
  show,
  handleClose,
  onSave,
  initialHyperparameters = {},
  modelId,
  projectAnnotationType,
}) => {
  // Generate hyperparameters based on project annotation type
  const getDefaultHyperparameters = (annotationType?: string): ModelHyperparameters => {
    const baseParams: ModelHyperparameters = {
      type:
        annotationType === 'bounding_box' || annotationType === 'oriented_bounding_box'
          ? 'object_detection'
          : 'classification',
      hyperparameters: {
        training: {
          epochs: {
            name: 'epochs',
            type: 'static' as const,
            input_type: 'integer' as const,
            value: 10,
            min_value: 1,
            max_value: 100,
          },
        },
        transforms: {},
      },
    }

    if (annotationType === 'classification') {
      return {
        ...baseParams,
        hyperparameters: {
          ...baseParams.hyperparameters,
          training: {
            ...baseParams.hyperparameters.training,
            learning_rate: {
              name: 'learning_rate',
              type: 'static' as const,
              input_type: 'float' as const,
              value: 0.001,
              min_value: 0.0001,
              max_value: 0.1,
            },
            batch_size: {
              name: 'batch_size',
              type: 'static' as const,
              input_type: 'integer' as const,
              value: 32,
              min_value: 1,
              max_value: 128,
            },
            optimizer: {
              name: 'optimizer',
              type: 'static' as const,
              input_type: 'string' as const,
              value: 'adam',
              allowed_values: ['adam', 'sgd', 'rmsprop'],
            },
            weight_decay: {
              name: 'weight_decay',
              type: 'static' as const,
              input_type: 'float' as const,
              value: 0.0001,
              min_value: 0.0,
              max_value: 0.01,
            },
            early_stopping: {
              name: 'early_stopping',
              type: 'static' as const,
              input_type: 'boolean' as const,
              value: true,
            },
            early_stopping_patience: {
              name: 'early_stopping_patience',
              type: 'static' as const,
              input_type: 'integer' as const,
              value: 5,
              min_value: 1,
              max_value: 20,
            },
          },
          transforms: {
            random_brightness_contrast: {
              enabled: {
                name: 'enabled',
                type: 'static' as const,
                input_type: 'boolean' as const,
                value: true,
              },
            },
            random_flip: {
              enabled: {
                name: 'enabled',
                type: 'static' as const,
                input_type: 'boolean' as const,
                value: true,
              },
            },
            random_rotation: {
              enabled: {
                name: 'enabled',
                type: 'static' as const,
                input_type: 'boolean' as const,
                value: false,
              },
            },
            normalize: {
              enabled: {
                name: 'enabled',
                type: 'static' as const,
                input_type: 'boolean' as const,
                value: true,
              },
            },
          },
        },
      }
    } else if (annotationType === 'bounding_box' || annotationType === 'oriented_bounding_box') {
      return {
        ...baseParams,
        hyperparameters: {
          ...baseParams.hyperparameters,
          training: {
            ...baseParams.hyperparameters.training,
            learning_rate: {
              name: 'learning_rate',
              type: 'static' as const,
              input_type: 'float' as const,
              value: 0.01,
              min_value: 0.0001,
              max_value: 0.1,
            },
            batch_size: {
              name: 'batch_size',
              type: 'static' as const,
              input_type: 'integer' as const,
              value: 16,
              min_value: 1,
              max_value: 64,
            },
            momentum: {
              name: 'momentum',
              type: 'static' as const,
              input_type: 'float' as const,
              value: 0.937,
              min_value: 0.0,
              max_value: 0.99,
            },
            weight_decay: {
              name: 'weight_decay',
              type: 'static' as const,
              input_type: 'float' as const,
              value: 0.0005,
              min_value: 0.0,
              max_value: 0.01,
            },
            iou_threshold: {
              name: 'iou_threshold',
              type: 'static' as const,
              input_type: 'float' as const,
              value: 0.5,
              min_value: 0.1,
              max_value: 0.9,
            },
            conf_threshold: {
              name: 'conf_threshold',
              type: 'static' as const,
              input_type: 'float' as const,
              value: 0.25,
              min_value: 0.01,
              max_value: 0.99,
            },
          },
          transforms: {
            mosaic: {
              enabled: {
                name: 'enabled',
                type: 'static' as const,
                input_type: 'boolean' as const,
                value: true,
              },
            },
            mixup: {
              enabled: {
                name: 'enabled',
                type: 'static' as const,
                input_type: 'boolean' as const,
                value: false,
              },
            },
            copy_paste: {
              enabled: {
                name: 'enabled',
                type: 'static' as const,
                input_type: 'boolean' as const,
                value: false,
              },
            },
            hsv_h: {
              value: {
                name: 'value',
                type: 'static' as const,
                input_type: 'float' as const,
                value: 0.015,
                min_value: 0.0,
                max_value: 0.1,
              },
            },
            hsv_s: {
              value: {
                name: 'value',
                type: 'static' as const,
                input_type: 'float' as const,
                value: 0.7,
                min_value: 0.0,
                max_value: 1.0,
              },
            },
            hsv_v: {
              value: {
                name: 'value',
                type: 'static' as const,
                input_type: 'float' as const,
                value: 0.4,
                min_value: 0.0,
                max_value: 1.0,
              },
            },
          },
        },
      }
    }

    return baseParams
  }

  const defaultTrainingParams = getDefaultHyperparameters(projectAnnotationType)

  const [hyperparameters, setHyperparameters] = useState<ModelHyperparameters | null>(null)
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({
    training: true,
    transforms: false,
  })

  const [openSubSections, setOpenSubSections] = useState<{
    [key: string]: boolean
  }>({})

  // Fetch default hyperparameters from API
  // Note: API might return different defaults (e.g., epochs: 100) than hardcoded (10)
  // Only fetch if we have a modelId (API requires it)
  const { data: defaultHyperparameters, isLoading: isLoadingHyperparameters } =
    useGetDefaultHyperparametersQuery(modelId || '', {
      skip: !modelId || !show,
    })

  // Store the initial hyperparameters in a ref to compare changes
  const previousHyperparametersRef = useRef(initialHyperparameters)
  const hasInitializedRef = useRef(false)
  const initialHyperparametersRef = useRef(initialHyperparameters)

  // Update the ref when initialHyperparameters changes (but don't reset state)
  useEffect(() => {
    initialHyperparametersRef.current = initialHyperparameters
  }, [initialHyperparameters])

  useEffect(() => {
    if (show) {
      // Re-initialize when modal opens OR when API defaults load
      // This ensures we always have all required fields from API defaults
      const currentInitial = initialHyperparametersRef.current

      // If we have a modelId and API defaults are still loading, wait for them
      // This prevents initializing with hardcoded defaults (epochs: 10) when API defaults (epochs: 100) are coming
      if (modelId && isLoadingHyperparameters) {
        // Don't initialize yet - wait for API defaults to load
        return
      }

      // Prioritize API defaults (they may differ from hardcoded, e.g., epochs: 100 vs 10)
      // API defaults take precedence over hardcoded defaults
      const apiHyperparameters = defaultHyperparameters
      const baseDefaults = apiHyperparameters || defaultTrainingParams

      // If we haven't initialized yet, initialize now
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true

        const combinedHyperparameters: ModelHyperparameters = {
          type: currentInitial?.type || baseDefaults.type || defaultTrainingParams.type,
          hyperparameters: {
            training: {
              // API defaults first (ensures all required fields are present), then hardcoded, then initial values
              ...(baseDefaults.hyperparameters?.training ||
                defaultTrainingParams.hyperparameters.training),
              ...(currentInitial?.hyperparameters?.training || {}),
            },
            transforms: {
              // API defaults first, then hardcoded, then initial values
              ...(baseDefaults.hyperparameters?.transforms ||
                defaultTrainingParams.hyperparameters.transforms),
              ...(currentInitial?.hyperparameters?.transforms || {}),
            },
          },
        }

        setHyperparameters(combinedHyperparameters)
        previousHyperparametersRef.current = currentInitial
      } else if (apiHyperparameters && !isLoadingHyperparameters) {
        // If API defaults loaded after initial initialization, re-initialize with correct values
        // This handles the case where API defaults finish loading after we've already initialized with hardcoded defaults
        const combinedHyperparameters: ModelHyperparameters = {
          type: currentInitial?.type || baseDefaults.type || defaultTrainingParams.type,
          hyperparameters: {
            training: {
              // API defaults first, then initial values (user's saved values take precedence)
              ...(baseDefaults.hyperparameters?.training ||
                defaultTrainingParams.hyperparameters.training),
              ...(currentInitial?.hyperparameters?.training || {}),
            },
            transforms: {
              // API defaults first, then initial values
              ...(baseDefaults.hyperparameters?.transforms ||
                defaultTrainingParams.hyperparameters.transforms),
              ...(currentInitial?.hyperparameters?.transforms || {}),
            },
          },
        }

        setHyperparameters(combinedHyperparameters)
      }
    }
  }, [show, defaultHyperparameters, isLoadingHyperparameters, modelId, projectAnnotationType])

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!show) {
      hasInitializedRef.current = false
      setHyperparameters(null)
      previousHyperparametersRef.current = {}
    }
  }, [show])

  // const handleInputChange = (
  //   section: 'training' | 'transforms',
  //   transformName: string | null,
  //   paramName: string,
  //   value: any
  // ) => {
  //   setHyperparameters((prev: ModelHyperparameters | null) => {
  //     if (!prev) return prev

  //     const newHyperparameters = {
  //       type: prev.type,
  //       hyperparameters: {
  //         training: { ...prev.hyperparameters.training },
  //         transforms: { ...prev.hyperparameters.transforms },
  //       },
  //     }

  //     if (section === 'training') {
  //       newHyperparameters.hyperparameters.training[paramName] = {
  //         ...prev.hyperparameters.training[paramName],
  //         value: value,
  //       }
  //     } else if (section === 'transforms' && transformName) {
  //       if (!newHyperparameters.hyperparameters.transforms[transformName]) {
  //         newHyperparameters.hyperparameters.transforms[transformName] = {}
  //       }
  //       newHyperparameters.hyperparameters.transforms[transformName] = {
  //         ...prev.hyperparameters.transforms[transformName],
  //       }
  //       newHyperparameters.hyperparameters.transforms[transformName][paramName] = {
  //         ...prev.hyperparameters.transforms[transformName][paramName],
  //         value: value,
  //       }
  //     }

  //     return newHyperparameters
  //   })
  // }

  const renderParameter = (
    param: ModelParameter,
    section: 'training' | 'transforms',
    transformName: string | null
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = param.input_type === 'boolean' ? e.target.checked : e.target.value
      let finalValue: number | boolean | string = value

      if (param.input_type === 'integer') {
        finalValue = parseInt(value as string, 10)
        if (param.min_value !== undefined && finalValue < param.min_value) {
          finalValue = param.min_value
        }
        if (param.max_value !== undefined && finalValue > param.max_value) {
          finalValue = param.max_value
        }
      } else if (param.input_type === 'float') {
        finalValue = parseFloat(value as string)
        if (param.min_value !== undefined && finalValue < param.min_value) {
          finalValue = param.min_value
        }
        if (param.max_value !== undefined && finalValue > param.max_value) {
          finalValue = param.max_value
        }
      }

      setHyperparameters((prev) => {
        if (!prev) return prev

        const newHyperparameters = { ...prev }
        if (section === 'training') {
          newHyperparameters.hyperparameters.training[param.name] = {
            ...param,
            value: finalValue,
          }
        } else if (transformName) {
          // Ensure the transformName exists before trying to access it
          if (!newHyperparameters.hyperparameters.transforms[transformName]) {
            newHyperparameters.hyperparameters.transforms[transformName] = {} as any // Initialize if missing
          }
          newHyperparameters.hyperparameters.transforms[transformName][param.name] = {
            ...param,
            value: finalValue,
          }
        }
        return newHyperparameters
      })
    }

    return (
      <Form.Group key={param.name} className="mb-3">
        <Form.Label className="d-flex justify-content-between align-items-center">
          <span>
            {param.name
              ?.split('_')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')}
          </span>
          {param.min_value !== undefined && param.max_value !== undefined && (
            <small className="text-muted">
              ({param.min_value} - {param.max_value})
            </small>
          )}
        </Form.Label>
        {param.input_type === 'boolean' ? (
          <Form.Check type="switch" checked={param.value as boolean} onChange={handleChange} />
        ) : (
          <Form.Control
            type={param.input_type === 'integer' ? 'number' : 'text'}
            value={String(param.value)}
            onChange={handleChange}
            min={param.min_value}
            max={param.max_value}
            step={param.input_type === 'float' ? 0.001 : 1}
          />
        )}
      </Form.Group>
    )
  }

  const handleSave = () => {
    if (hyperparameters) {
      // Ensure we merge with API defaults one final time before saving
      // This guarantees all required fields (name, type, input_type) are present
      const apiHyperparameters = defaultHyperparameters
      const baseDefaults = apiHyperparameters || defaultTrainingParams

      // Merge API defaults with current hyperparameters to ensure all required fields
      const finalHyperparameters: ModelHyperparameters = {
        type: hyperparameters.type,
        hyperparameters: {
          training: {
            // Start with API defaults to ensure all required fields (name, type, input_type) are present
            ...(baseDefaults.hyperparameters?.training ||
              defaultTrainingParams.hyperparameters.training),
            // Then override with user's configured values
            ...(hyperparameters.hyperparameters.training || {}),
          },
          transforms: {
            ...(baseDefaults.hyperparameters?.transforms ||
              defaultTrainingParams.hyperparameters.transforms),
            ...(hyperparameters.hyperparameters.transforms || {}),
          },
        },
      }

      onSave({
        hyperparameters: finalHyperparameters,
      })
    }
    handleClose()
  }

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const toggleSubSection = (transformName: string) => {
    setOpenSubSections((prev) => ({
      ...prev,
      [transformName]: !prev[transformName],
    }))
  }

  return (
    <StyledModal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Model Hyperparameters {hyperparameters?.type ? `- ${hyperparameters.type}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoadingHyperparameters ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading hyperparameters...</span>
            </div>
            <p className="mt-2 text-muted">Loading hyperparameters...</p>
          </div>
        ) : (
          <Form>
            {hyperparameters?.hyperparameters?.training &&
              Object.keys(hyperparameters.hyperparameters.training).length > 0 && (
                <Section key="training-section">
                  <h4 onClick={() => toggleSection('training')}>
                    <FaChevronRight className={`chevron ${openSections.training ? 'open' : ''}`} />
                    Training Parameters
                  </h4>
                  <Collapse in={openSections.training}>
                    <div className="section-content">
                      {Object.entries(hyperparameters.hyperparameters.training)?.map(
                        ([key, param]) => (
                          <div key={`training-${key}`}>
                            {renderParameter(param, 'training', null)}
                          </div>
                        )
                      )}
                    </div>
                  </Collapse>
                </Section>
              )}

            {hyperparameters?.hyperparameters?.transforms &&
              Object.keys(hyperparameters.hyperparameters.transforms).length > 0 && (
                <Section key="transforms-section">
                  <h4 onClick={() => toggleSection('transforms')}>
                    <FaChevronRight
                      className={`chevron ${openSections.transforms ? 'open' : ''}`}
                    />
                    Transform Parameters
                  </h4>
                  <Collapse in={openSections.transforms}>
                    <div className="section-content">
                      {Object.entries(hyperparameters.hyperparameters.transforms)?.map(
                        ([transformName, params]) => (
                          <SubSection key={`transform-${transformName}`}>
                            <h5 onClick={() => toggleSubSection(transformName)}>
                              <FaChevronRight
                                className={`chevron ${
                                  openSubSections[transformName] ? 'open' : ''
                                }`}
                              />
                              {(transformName?.split('_') || [])
                                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ')}
                            </h5>
                            <Collapse in={openSubSections[transformName]}>
                              <div className="subsection-content">
                                {Object.entries(params || {}).map(([key, param]) => (
                                  <div key={`transform-${transformName}-${key}`}>
                                    {renderParameter(param, 'transforms', transformName)}
                                  </div>
                                ))}
                              </div>
                            </Collapse>
                          </SubSection>
                        )
                      )}
                    </div>
                  </Collapse>
                </Section>
              )}
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save Changes
        </Button>
      </Modal.Footer>
    </StyledModal>
  )
}

export default HyperparameterSettingsModal
